<!-- App.vue -->
<template>
  <component :is="layoutComponent" />

  <!-- Global Notifications Component -->
  <Notifications />

  <!-- Deposit Modal -->
    <div
      class="modal fade"
      id="DepositModal"
      data-keyboard="false"
      data-backdrop="static"
      tabindex="-1"
      role="dialog"
      aria-labelledby="DepositModal"
      aria-hidden="true"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h4>
              <span class="icon"><img :src="icons.moneyBag" alt="money icon" /></span>
              Deposit money into your account
            </h4>
            <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
              <span aria-hidden="true"><i class="bi bi-x"></i></span>
            </button>
          </div>

          <div class="modal-body">
            <div class="deposit-wrapper">
              <div class="container">
                <div class="row">
                  <!-- Presets -->
                  <div class="col-12">
                    <div class="choose-amount">
                      <p class="pb-2" style="font-size: 14px">Choose Amount</p>
                      <div class="choose-amount-to-deposit">
                        <button
                          v-for="preset in presetAmounts"
                          :key="preset"
                          type="button"
                          class="btn"
                          :class="{ active: Number(depositAmount) === preset }"
                          @click="depositAmount = String(preset)"
                        >
                          {{ preset }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Manual entry -->
                  <div class="col-12">
                    <div class="choose-amount">
                      <p>Or</p>
                      <p>Enter the amount to Deposit</p>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="amount-field">
                      <div class="input-group mb-2">
                        <input
                          v-model="depositAmount"
                          type="text"
                          class="form-control form-control-lg"
                          :class="{ 'is-invalid': validationError }"
                          placeholder="Enter amount"
                          inputmode="numeric"
                          @keypress="preventDecimal"
                        />
                        <div v-if="validationError" class="invalid-feedback">
                          {{ validationError }}
                        </div>
                      </div>
                      <button
                        type="button"
                        class="btn btn-block"
                        :disabled="isProcessing || !depositAmount"
                        @click="handleDeposit"
                      >
                        {{ isProcessing ? 'Processing...' : 'Deposit' }}
                      </button>
                    </div>
                  </div>

                  <!-- Limits -->
                  <div class="col-12">
                    <div class="choose-amount">
                      <p>
                        Maximum daily transaction value is
                        {{ limits.currencyLabel }} {{ limits.maxDaily.toLocaleString() }}
                      </p>
                      <p>
                        Maximum value per transaction is
                        {{ limits.currencyLabel }} {{ limits.maxPerTxn.toLocaleString() }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- /.deposit-wrapper -->
          </div>
        </div>
      </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { CDN } from '@/config/cdn'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import HomeLayout from '@/layouts/HomeLayout.vue'
import LobbyLayout from '@/layouts/LobbyLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import GameLayout from '@/layouts/GameLayout.vue'
import IframeLayout from '@/layouts/IframeLayout.vue'
import { useDepositStore } from '@/stores/useDepositStore'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { DEPOSIT_LIMITS, DEPOSIT_PRESETS } from '@/constants/payments'
import { useGlobalBetslipWatcher } from '@/composables/useGlobalBetslipWatcher'
import Notifications from '@/components/Notifications.vue'

const route = useRoute()
const layoutComponent = computed(() => {
  const key = route.meta?.layout
  if (key === 'home') return HomeLayout
  if (key === 'lobby') return LobbyLayout
  if (key === 'auth') return AuthLayout
  if (key === 'game') return GameLayout
  if (key === 'iframe') return IframeLayout
  return DefaultLayout
})

// Initialize global betslip watcher for odds updates and expiry checks
const { initialize: initBetslipWatcher } = useGlobalBetslipWatcher()

onMounted(() => {
  // Initialize the global betslip watcher
  initBetslipWatcher()
})

const icons = { moneyBag: CDN.icons('money-bag.png') }
const limits = DEPOSIT_LIMITS

// Stores
const depositStore = useDepositStore()
const notificationStore = useNotificationStore()

// UI state
const depositAmount = ref('')
const isProcessing = ref(false)
const validationError = ref('')
const presetAmounts = DEPOSIT_PRESETS

function preventDecimal(e) {
  if (e.key === '.' || e.key === ',') e.preventDefault()
}

function closeBootstrapModalById(id) {
  const el = document.getElementById(id)
  if (!el) return
  if (window.bootstrap?.Modal) {
    const instance = window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el)
    instance.hide()
  } else {
    el.classList.remove('show')
    el.style.display = 'none'
    document.body.classList.remove('modal-open')
    document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove())
  }
}

const handleDeposit = async () => {
  validationError.value = ''
  const n = Number(depositAmount.value)

  if (!Number.isInteger(n)) {
    validationError.value = 'Please enter a whole number amount'
    return
  }

  isProcessing.value = true
  try {
    const result = await depositStore.submitOnlineDeposit(n)
    if (result.success) {
      const prev = n
      depositAmount.value = ''
      closeBootstrapModalById('DepositModal')
      notificationStore.showNotification(
        `Deposit of ${limits.currencyLabel} ${prev} initiated successfully!`,
        'success',
      )
    } else {
      validationError.value = result.message || 'Deposit failed'
    }
  } finally {
    isProcessing.value = false
  }
}
</script>
